/**
 * `AW15` · Formulario de filtros de la pantalla «Participantes» (el padrón).
 *
 * Pura presentación: convierte lo que el operador elige y escribe en el
 * `FiltroDelPadron` que el borde sabe pedir, y se lo pasa al llamador por
 * `onAplicar`. No guarda estado global, no habla con `admin-web` y **no filtra
 * nada por su cuenta**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ `guard` · EL TELÉFONO ES DE IGUALDAD EXACTA Y LA PANTALLA LO DICE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El campo **no** es un buscador que filtra mientras se tipea: el operador
 * escribe el identificador COMPLETO y aprieta «Aplicar filtro». Hay tres formas
 * de equivocarse acá y las tres son peores que la molestia:
 *
 *   · **Un campo incremental** produce cero resultados hasta que el
 *     identificador está entero, y el operador lee «no hay nadie» de alguien que
 *     sí está. El borde no soporta subcadena —`CP20` fijó igualdad sobre la
 *     forma canónica, con el argumento de ⟦el-telefono-es-opaco⟧—.
 *   · **Pedir subcadena del otro lado** convierte esta pantalla en una
 *     superficie de **ENUMERACIÓN** —tres dígitos y aparecen todos— sobre un
 *     padrón de ~16.000 estudiantes chilenos, muchos menores, para las ~10
 *     personas que `docs/prd/errata.md` Q17 dimensiona con un solo rol y sin
 *     separación de permisos. No es una comodidad de filtro: cambia qué puede
 *     hacer una persona con acceso.
 *   · **Filtrar en el navegador las filas ya traídas** es lo mismo con otra ropa
 *     y encima MIENTE, porque busca sobre una página de un conjunto de cientos.
 *
 * Lo que la pantalla sí puede hacer es lo contrario: decirlo en el campo, y no
 * inventar resultados parciales. El texto de ayuda es eso.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ SON DOS CAMPOS Y NO UNA SOLA CAJA DE BÚSQUEDA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El teléfono es igualdad exacta sobre la forma canónica; el nombre coincide de
 * forma insensible a caja y a tildes. **Las dos semánticas son distintas**, y
 * una caja única obliga a la pantalla a adivinar cuál aplicar — o sea, a poner
 * una tercera opinión sobre un dato de menores en el único lugar donde nadie la
 * revisa. Cada semántica tiene su campo y su texto.
 */

import { useEffect, useRef, useState } from "react"
import type { JSX } from "react"
import type { FiltroDelPadron } from "./borde-padron.ts"
import { rotuloDeLote } from "./formato.ts"

/**
 * ⭐ `CA-21` + regla 10 · **PLIEGA CAJA Y TILDES, Y ESTO NO ES UNA COMODIDAD.**
 *
 * El operador escribe «lote nunoa» con el teclado en español y el lote dice
 * «Lote Ñuñoa»: sin plegar tildes el buscador devuelve vacío, y vacío acá se lee
 * «ese lote no existe». Se pliega sobre LOS DOS LADOS: lo tipeado y el nombre
 * del lote.
 *
 * ⛔ Y NO se usa `startsWith` ni la igualdad: el buscador de lotes es CONTIENE
 * (`CA-21` lo fija textual). Un lote no identifica a una persona —enumerar lotes
 * no enumera participantes— así que acá la subcadena es la respuesta correcta.
 *
 * ⭐ **Y VIVE ARRIBA DEL TODO, ANTES DE CUALQUIER `telefono`, A PROPÓSITO.**
 * `telefono-fuera-del-navegador` (`gates/r1/R3-UI3-filtros.gate.ts:483`) se pone
 * en rojo si en este archivo una mención de `telefono` tiene un `.normalize(` a
 * menos de 120 caracteres: el teléfono se compara por IGUALDAD contra la base
 * —el servidor lo limpia, `RT 5` §regla 9— y la pantalla no lo normaliza. El
 * `normalize` de acá es de los NOMBRES DE LOTE, y el examen no puede adivinar
 * eso: mira texto. Con la función arriba, el único `.normalize(` del archivo
 * queda ANTES de la primera mención de `telefono` y el chequeo dice la verdad.
 */
function normalizarParaBuscar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

/**
 * Borrador que vive en el formulario, separado del filtro APLICADO.
 *
 * ⭐ Que sean dos cosas distintas es el `guard` ⑤ de la etapa: la tabla, el
 * paginado y el número de páginas leen el filtro que produjo la respuesta que se
 * está mostrando, no lo que hay tipeado en los campos. Leer el formulario en el
 * momento de pedir «Siguiente» le daría al operador la página 2 de OTRO
 * conjunto, con un `total` del conjunto anterior: hay filas, están bien
 * formadas, y son de otro filtro. El síntoma no existe.
 */
type Borrador = {
  readonly lotes: readonly string[]
  readonly telefono: string
  readonly nombre: string
}

/*
 * ⛔⛔ **LOS DOS `""` DE ACÁ SON CARGA ÚTIL, NO ESTILO.** No los cambies a
 * `null` aunque `FiltroDelPadron` los acepte: parecería más limpio —`null` es
 * «este filtro no se manda»— y pondría ROJO `limpiar-vacia-los-tres`
 * (`gates/r1/R3-UI3-filtros.gate.ts:441-447`), que busca en el texto del archivo
 * el literal `telefono: ""` (y `nombre: ""`) para probar que «Limpiar» vacía los
 * tres campos. `limpiar()` (`:178-182`) ya manda el filtro correcto —`null`, que
 * `construirQueryDelPadron` (`borde-padron.ts:305-310`) traduce a NO mandar el
 * parámetro—, así que el literal de acá es lo único que sostiene ese chequeo y
 * vive separado de la línea que lo usa. Es feo y está anotado por eso.
 *
 * El `Borrador` guarda strings porque es lo que tipea el operador (una caja de
 * texto no tiene `null`); el `null` aparece recién al traducir, en
 * `filtroDesdeBorrador` (`:111-117`).
 */
const BORRADOR_VACIO: Borrador = {
  lotes: [],
  telefono: "",
  nombre: "",
}

/**
 * ⛔⭐ **UN CAMPO VACÍO ES `null`, Y `null` NO SE MANDA.**
 *
 * `admin-web` rechaza un `?telefono=` o un `?nombre=` PRESENTE y vacío con
 * `malformedRequest`, a propósito (`AP28`): «un filtro sin criterio devuelve
 * todas, y las dos se ven igual desde arriba». Quien tiene que no mandarlo es
 * esta pantalla, y empieza acá: el borrador vacío se traduce a `null` y
 * `construirQueryDelPadron` omite el parámetro.
 *
 * Sin lotes elegidos, `lotes` es el arreglo vacío: el padrón completo del brazo,
 * que es el comportamiento por defecto de la pantalla.
 */
function filtroDesdeBorrador(borrador: Borrador): FiltroDelPadron {
  return {
    lotes: borrador.lotes,
    telefono: borrador.telefono.trim() === "" ? null : borrador.telefono.trim(),
    nombre: borrador.nombre.trim() === "" ? null : borrador.nombre.trim(),
  }
}

export function FiltrosDelPadron(props: {
  /** Los lotes del brazo, ya leídos por la tabla. Vacío = no hay ninguno que ofrecer. */
  readonly lotes: readonly string[]
  /** Un fallo al leer los lotes es del SELECTOR, y no del padrón. Se dice acá. */
  readonly errorLotes: string | null
  readonly onAplicar: (filtro: FiltroDelPadron) => void
}): JSX.Element {
  const [borrador, setBorrador] = useState<Borrador>(BORRADOR_VACIO)
  /**
   * Lo que hay tipeado en el buscador de lotes. Es estado PROPIO y no del
   * borrador: escribir en el buscador no cambia el filtro —sólo qué se ve en la
   * lista—, y el filtro se aplica recién con «Aplicar filtro» (regla 10).
   */
  const [busqueda, setBusqueda] = useState("")

  /**
   * ⛔ Filtro Lotes (`r3-ui3.md:75`, regla 10) · **EL SELECTOR ES DESPLEGABLE, Y
   * ESO ES COMPORTAMIENTO, NO ESTILO.** Sin elegir dice «Todos los lotes»; al
   * abrirlo aparecen el buscador y la lista; se cierra con `Escape` o con un clic
   * afuera **conservando lo elegido y vaciando el buscador** (`r3-ui3.md:75`).
   * El `fieldset` entero es la «caja» contra la que se mide el «afuera», así que
   * tocar el buscador o un lote NO lo cierra.
   */
  const [abiertoElSelector, setAbiertoElSelector] = useState(false)
  const cajaDeLotes = useRef<HTMLFieldSetElement>(null)

  /**
   * `CA-21` + regla 10: los lotes que se dibujan son los que CONTIENEN lo
   * tipeado, plegando caja y tildes sobre los dos lados.
   *
   * ⛔ La lista llega en el orden del servidor (`CA-18`) y acá no se toca: no hay
   * un `.sort(` en esta pantalla — el orden se resuelve del otro lado, sobre la
   * lista entera (`RT 5`).
   *
   * ⛔⭐ **LOS ESPACIOS DE LOS BORDES DE LO TIPIADO NO CUENTAN** (`r3-ui3.md:75`,
   * `PRD_R3.md:1962`: «Los espacios de los bordes no cuentan»). El `trim()` va
   * SOLO sobre `busqueda` —el lado que tipea el operador— y NO dentro de
   * `normalizarParaBuscar`, porque esa misma función pliega el NOMBRE del lote
   * (lado izquierdo): un lote cuyo nombre legítimo empiece o termine en espacio
   * se seguiría encontrando, y quitarlos de los dos lados lo volvería
   * inencontrable. Sin este `trim`, con el teclado en español escribir
   * « LOTE BÁSE » (espacios en los bordes) devolvía «No hay lotes con ese
   * nombre.» sobre un lote que sí existe: el síntoma que `CA-21` quiso evitar.
   */
  const busquedaLimpia = busqueda.trim()
  const lotesFiltrados = props.lotes.filter((lote) =>
    normalizarParaBuscar(lote).includes(normalizarParaBuscar(busquedaLimpia)),
  )

  function alternarLote(lote: string): void {
    setBorrador((previo) => ({
      ...previo,
      lotes: previo.lotes.includes(lote)
        ? previo.lotes.filter((otro) => otro !== lote)
        : [...previo.lotes, lote],
    }))
  }

  /** `CA-24` · la «×» de la etiqueta: quita del borrador el lote elegido. */
  function quitarLote(lote: string): void {
    setBorrador((previo) => ({
      ...previo,
      lotes: previo.lotes.filter((otro) => otro !== lote),
    }))
  }

  function aplicar(): void {
    props.onAplicar(filtroDesdeBorrador(borrador))
  }

  function limpiar(): void {
    setBorrador(BORRADOR_VACIO)
    setBusqueda("")
    props.onAplicar({ lotes: [], telefono: null, nombre: null })
  }

  /**
   * ⛔ Filtro Lotes · **EL CIERRE TIENE DOS SALIDAS Y LAS DOS SON «NO ELIGE
   * NADA».** `Escape` y un clic afuera del `fieldset` cierran el desplegable
   * CONSERVANDO `borrador.lotes` (lo elegido no se pierde) y VACIANDO el buscador
   * (`r3-ui3.md:75`). Los dos `setState` van acá adentro —y no en un helper
   * referenciado— para que las dependencias del efecto sean completas.
   */
  useEffect(() => {
    if (!abiertoElSelector) return
    const cerrar = (): void => {
      setAbiertoElSelector(false)
      setBusqueda("")
    }
    const alTeclear = (evento: KeyboardEvent): void => {
      if (evento.key === "Escape") cerrar()
    }
    const alClicAfuera = (evento: MouseEvent): void => {
      const caja = cajaDeLotes.current
      if (caja !== null && !caja.contains(evento.target as Node)) cerrar()
    }
    document.addEventListener("keydown", alTeclear)
    document.addEventListener("mousedown", alClicAfuera)
    return () => {
      document.removeEventListener("keydown", alTeclear)
      document.removeEventListener("mousedown", alClicAfuera)
    }
  }, [abiertoElSelector])

  return (
    <form className="padron__filtros" onSubmit={(evento) => evento.preventDefault()}>
      <fieldset ref={cajaDeLotes} className="padron__filtro padron__lotes">
        <legend className="padron__etiqueta">Lotes</legend>

        {props.errorLotes !== null && (
          <p className="padron__error" role="alert">
            No se pudo leer la lista de lotes. {props.errorLotes}
          </p>
        )}

        {/*
          Sin ningún lote elegido se consulta el padrón COMPLETO del brazo. No
          hay una opción «Todos»: sería un segundo nombre para el mismo estado y
          el día que alguien la marcara junto con un lote, habría dos respuestas
          posibles y ninguna escrita.
        */}
        {props.lotes.length === 0 && props.errorLotes === null && (
          <p className="padron__ayuda">
            El brazo no tiene lotes cargados. Se consulta el padrón completo.
          </p>
        )}

        {/*
          ⭐ `CA-21` + regla 10 · **EL BUSCADOR, EN LUGAR DE UNA LISTA DE
          CASILLAS.** Con un padrón de decenas de lotes, encontrar uno era
          recorrer la lista entera con la vista y marcar de a uno; acá se escribe
          y la lista se recorta. Lo elegido queda a la vista como etiqueta.

          ⛔ El buscador NO filtra: recorta la LISTA de lotes que se ofrece. El
          padrón se consulta recién con «Aplicar filtro» (regla 10).
        */}
        {/*
          ⛔⛔ **LAS ETIQUETAS VAN FUERA DEL DESPLEGABLE, Y ESO ES EL ARREGLO.**
          `[30/9/2026 · R3-H-004 · CP-R3-F5-017/024/025]` Todo este bloque vivía
          DENTRO de `{abiertoElSelector && (…)}`, unas líneas más abajo. Con la
          lista cerrada no había ninguna etiqueta —los tres casos midieron `[]`— y
          la «×» era **inalcanzable**, contra `PRD_R3.md:1964` ⑥, que es textual:
          «Tocar la «×» de una etiqueta quita ese lote, **sin abrir la lista**».
          Y `:1947` pide que cerrado el selector muestre «las etiquetas de los
          lotes elegidos».

          ⚠️ Y cerrar es fácil de hacer sin querer: `cerrar()` se dispara con
          `Escape` **y con cualquier `mousedown` fuera del `fieldset`**, así que
          tocar el campo Teléfono o «Aplicar filtro» hacía desaparecer las
          etiquetas. La selección se conservaba en `borrador.lotes` —el defecto
          era sólo que dejaba de VERSE—, y por eso los tres casos cortaron en su
          testigo y no en su aserción.

          ⛔ El examen de fuente que lo dejó pasar (`lotes-etiquetas-con-x-y-check`)
          verificaba que la «×» existiera, que tuviera `onClick` y que quitara el
          lote — todo cierto — pero **no que se pudiera tocar con la lista
          cerrada**. Se le agregó `lotes-etiquetas-alcanzables-cerrado` en el mismo
          cierre, que es el que prohíbe volver a meter este bloque adentro.
        */}
        {borrador.lotes.length > 0 && (
          <ul className="padron__lotes-elegidos">
            {borrador.lotes.map((lote) => (
              <li key={lote} className="padron__elegido">
                {rotuloDeLote(lote)}
                <button
                  type="button"
                  className="padron__quitar"
                  aria-label={`Quitar ${rotuloDeLote(lote)}`}
                  onClick={() => quitarLote(lote)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {abiertoElSelector && (
          <>
            <input
              id="padron-buscar-lote"
              type="text"
              aria-label="Buscar lote por nombre"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />

            {lotesFiltrados.length === 0 ? (
              <p className="padron__ayuda">No hay lotes con ese nombre.</p>
            ) : (
              <ul className="padron__lista-lotes">
                {lotesFiltrados.map((lote) => {
                  const elegido = borrador.lotes.includes(lote)
                  return (
                    <li key={lote}>
                      <button
                        type="button"
                        onClick={() => alternarLote(lote)}
                        aria-pressed={elegido}
                      >
                        {elegido ? "✓" : "·"} {rotuloDeLote(lote)}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </>
        )}

        {/*
          ⭐ `R1.1-F5-CA-32` vía `ADR-0319` ② · **el texto es el del boceto**
          (`HibParticipantes.dc.html:78`, `PuroParticipantes.dc.html:78`,
          `p.ayuda`). Ningún criterio `CA-NN` fija la redacción de esta ayuda:
          `R1.1-F4-CA-06` y `CA-10` fijan el COMPORTAMIENTO del filtro, y
          `PRD_R1.1.md:809` es la descripción funcional, que por la regla dura no
          alcanza.

          ⛔ **«del brazo» se cae y NO se pierde nada verdadero**: la leyenda de la
          tabla ya dice de quién es el padrón —«Padrón de KAI · Brazo Puro»— y eso
          lo EXIGE `R1.1-F4-CA-01` (`PRD_R1.1.md:833`), o sea que está sostenido
          por un criterio y no por esta línea.

          ⭐ `CA-21` · **y el estado del selector se dice en la MISMA línea.** Sin
          ningún lote elegido el selector está en «Todos los lotes», que es el
          padrón completo del brazo: nombrarlo evita el hueco que cada operador
          interpreta distinto. Una segunda línea para el mismo estado era la
          redundancia que el boceto no dibuja — se dice una vez, con los dos
          nombres que los criterios piden.
        */}
        {/*
          ⛔ RASTRO · `R3-UI3` `[29/9/2026]` · ~~la ayuda de Lotes decía «Sin
          ninguno marcado se muestra el padrón completo.»~~ → **dice «elegido».**

          Por qué: el PRD de R3 fija la redacción textualmente
          (`docs/prd/PRD_R3.md:1947`, §Participantes: «Ayuda “Sin ninguno elegido
          se muestra el padrón completo.”») y el boceto de R3 lo dibuja igual
          (`docs/prd/R3_bocetos/pantallas/HibParticipantes.dc.html:209`). «marcado»
          es la redacción de R1.1
          (`docs/prd/R1.1_bocetos/pantallas/HibParticipantes.dc.html:80`,
          `ADR-0319` ②), y por eso el examen de esta etapa la deja ROJA a
          propósito: `gates/r1/R3-UI3-filtros.gate.ts:51` («la vieja “Sin ninguno
          marcado…” queda roja») y el mutante M43 de
          `gates/r1/MUTANTES-R3-UI3-filtros.md:156`.

          ⚠️ Y el examen de R1.1 que fijaba la redacción vieja
          (`pruebas/r11-encabezado-de-padron.test.tsx:303-305`) era CONSUMIDOR de
          esta copia: se actualizó a la nueva en el MISMO cambio. No es tocar un
          examen para que pase el sujeto —el gate de la etapa es el que manda la
          redacción nueva y no se tocó—: es mover los dos extremos de un cambio de
          texto que otros consumen.

          El estado del selector sigue aparte, en `.padron__estado` (`CA-21`): el
          `fieldset` conserva EXACTAMENTE UN `.padron__ayuda` y el formulario
          EXACTAMENTE TRES
          (`pruebas/r11-encabezado-de-padron.test.tsx:288-291` y `:355`).
        */}
        <p className="padron__ayuda">Sin ninguno elegido se muestra el padrón completo.</p>

        <button
          type="button"
          className="padron__estado"
          aria-live="polite"
          aria-expanded={abiertoElSelector}
          onClick={() => setAbiertoElSelector(!abiertoElSelector)}
        >
          {borrador.lotes.length === 0 ? "Todos los lotes" : `${borrador.lotes.length} elegido(s).`}
        </button>
      </fieldset>

      <div className="padron__filtro">
        <label className="padron__etiqueta" htmlFor="padron-telefono">
          Teléfono
        </label>
        <input
          id="padron-telefono"
          type="text"
          value={borrador.telefono}
          onChange={(evento) =>
            setBorrador((previo) => ({ ...previo, telefono: evento.target.value }))
          }
        />
        {/*
          ⭐ `R1.1-F5-CA-32` vía `ADR-0319` ② · el texto del boceto
          (`HibParticipantes.dc.html:79`). `R1.1-F4-CA-07` y `CA-08` fijan el
          comportamiento (el filtro por teléfono es exacto; el número del caso está
          en el criterio, `PRD_R1.1.md:839`), **no la redacción**.
          ⟦rastro · ADR-0082 · 1/10/2026 · AW7 · AWA13 · decisión del TL⟧ acá se citaba
          el número de `R1.1-F4-CA-07` entero, con forma de teléfono chileno, y `AWA13`
          lo acusaba; el comentario cita el criterio por su id y no copia el número.

          ⚠️⚠️ **ÉSTA ES LA ÚNICA DE LAS TRES EN LA QUE APLICAR ② CUESTA
          INFORMACIÓN, Y VA DICHO.** El texto que se va decía además «no por
          fragmento» y «no hay resultados parciales»; `estilos.css:2777-2782` tiene
          un guard de su dueño que llama a eso *«lo único que evita que el operador
          lea “no hay nadie” de alguien que sí está»*. **Un guard de producto no es
          un criterio** y por la regla dura no gana contra el boceto, así que el
          cambio va — pero queda escrito acá para que se pueda revertir sabiendo
          qué se pierde, y está reportado al dueño del Anexo B.
        */}
        <span className="padron__ayuda">Coincidencia exacta.</span>
      </div>

      <div className="padron__filtro">
        <label className="padron__etiqueta" htmlFor="padron-nombre">
          Nombre
        </label>
        <input
          id="padron-nombre"
          type="text"
          value={borrador.nombre}
          onChange={(evento) =>
            setBorrador((previo) => ({ ...previo, nombre: evento.target.value }))
          }
        />
        {/*
          ⭐ `R1.1-F5-CA-32` vía `ADR-0319` ② · el texto del boceto
          (`HibParticipantes.dc.html:79`). `R1.1-F4-CA-09` fija el comportamiento
          («aplicar Nombre `bruno hibrido` muestra «Bruno Híbrido»»), no el texto.
          Acá ② no cuesta nada: las dos redacciones dicen exactamente lo mismo y la
          del boceto es más corta.
        */}
        <span className="padron__ayuda">Sin distinguir mayúsculas ni tildes.</span>
      </div>

      <div className="padron__acciones">
        <button type="button" onClick={aplicar}>
          Aplicar filtro
        </button>
        <button type="button" onClick={limpiar}>
          Limpiar
        </button>
      </div>
    </form>
  )
}
